import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { useEffect, useState } from "react"
import { RoutesLink } from "@/utility/RouteLinks"
import { ThrowError } from "@/api/callApi"
import { ApplicationService } from "@/services/Applications/ApplicationService"
import { showToastNotification } from "@/utility/helper"
import { errorTypes } from "@/utility/validation"
import FormField from "@/components/common/FormField"
import MultiSelectSearch from "@/components/common/MultiSelectSearch"
import Card from "@/components/common/Card"
import { Button } from "@/components/ui/button"
import type { Application } from "@/interface/applications.interface"
import { Plus, X } from "lucide-react"

const AddApplication = () => {
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<Application>({
        defaultValues: {
            allowedScopes: ["openid", "profile", "email"],
            redirectUris: [],
            siteId: ""
        }
    })
    const navigate = useNavigate()
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [newUri, setNewUri] = useState("")
    const appName = watch("name")
    const redirectUris = watch("redirectUris") || []

    // Helper to generate random alphanumeric string
    const generateAlphanumeric = (length: number) => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };


    const getApplicationById = async () => {
        if (!id) return
        setIsLoading(true)
        try {
            const response = await ApplicationService.getApplicationById(id)
            if (response?.status) {
                const data = response.data
                setValue("name", data.name)
                setValue("clientId", data.clientId)
                // setValue("siteId", data.siteId)
                setValue("isActive", data.isActive)
                setValue("allowedScopes", data.allowedScopes)
                setValue("redirectUris", data.redirectUris)
                // Secret logic (usually not returned by GET or kept masked)
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (id) {
            getApplicationById()
        }
    }, [id])

    // Auto-generate ClientID and Secret based on Name
    useEffect(() => {
        if (!id && appName && appName.trim().length > 2) {
            const prefix = appName.trim().substring(0, 3).toUpperCase();
            const randomId = `${prefix}_${generateAlphanumeric(8)}`;
            setValue("clientId", randomId);
            setValue("clientSecret", generateAlphanumeric(16));
        }
    }, [appName, id, setValue]);

    useEffect(() => {
        // register("siteId", { ...errorTypes.required("Site") })
        register("allowedScopes", { required: "At least one scope is required" })
        register("redirectUris", { validate: (v) => (v && v.length > 0) || "At least one Redirect URI is required" })
    }, [register])

    const onSubmit = async (data: Application) => {
        setIsLoading(true)
        try {
            const payload = {
                ...data,
                isActive: String(data.isActive) === "true",
                siteId: ""
            }
            if (id) {
                const response = await ApplicationService.updateApplication(id, payload)
                if (response?.status) {
                    showToastNotification("success", response.message)
                    navigate(RoutesLink.superAdminApplication)
                }
            } else {
                const response = await ApplicationService.createApplication(payload)
                if (response?.status) {
                    showToastNotification("success", response.message)
                    navigate(RoutesLink.superAdminApplication)
                }
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleAddUri = () => {
        if (newUri && !redirectUris.includes(newUri)) {
            setValue("redirectUris", [...redirectUris, newUri], { shouldValidate: true });
            setNewUri("");
        }
    };

    const handleRemoveUri = (uri: string) => {
        setValue("redirectUris", redirectUris.filter(u => u !== uri), { shouldValidate: true });
    };

    const scopeOptions = [
        { value: "openid", label: "OpenID" },
        { value: "profile", label: "Profile" },
        { value: "email", label: "Email" },
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title={id ? "Edit Application" : "Add Application"}
                    isBackButtonVisible={true}
                    onClick={() => navigate(-1)}
                />

                <Card className="overflow-visible">
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("name", { ...errorTypes.required("Application Name") })}
                                label="Application Name"
                                placeholder="e.g. Google"
                                error={errors.name?.message}
                            />

                            <FormField
                                {...register("clientId")}
                                label="Client ID"
                                placeholder="Auto-generated"
                                disabled
                                error={errors.clientId?.message}
                            />

                            <FormField
                                {...register("clientSecret", { ...errorTypes.required("Client Secret") })}
                                label="Client Secret"
                                placeholder="Auto-generated (Editable)"
                                error={errors.clientSecret?.message}
                            />

                            {/* <SearchSelect
                                label="Site"
                                required
                                placeholder="Select Site"
                                options={sites}
                                value={watch("siteId")}
                                onChange={(val) => setValue("siteId", val, { shouldValidate: true })}
                                error={errors.siteId?.message}
                            /> */}

                            <MultiSelectSearch
                                label="Allowed Scopes"
                                required
                                placeholder="Select Scopes"
                                options={scopeOptions}
                                value={watch("allowedScopes") || []}
                                onChange={(val) => setValue("allowedScopes", val, { shouldValidate: true })}
                                error={errors.allowedScopes?.message}
                            />

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Redirect URIs</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newUri}
                                        onChange={(e) => setNewUri(e.target.value)}
                                        placeholder="http://localhost:3000"
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    />
                                    <Button type="button" onClick={handleAddUri} size="icon" className="shrink-0">
                                        <Plus size={18} />
                                    </Button>
                                </div>
                                {errors.redirectUris && <p className="text-xs text-destructive mt-1">{errors.redirectUris.message}</p>}

                                <div className="flex flex-wrap gap-2 mt-3">
                                    {redirectUris.map((uri) => (
                                        <div key={uri} className="flex items-center gap-2 bg-primary/5 text-primary border border-primary/20 px-3 py-1.5 rounded-lg text-sm group">
                                            <span>{uri}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveUri(uri)}
                                                className="text-primary/50 hover:text-destructive transition-colors"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <FormField
                                {...register("isActive", { ...errorTypes.required("Status") })}
                                type="select"
                                label="Status"
                                value={String(watch("isActive") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                error={errors.isActive?.message}
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-8">
                            <Button variant="outline" type="button" onClick={() => navigate(-1)} disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? "Saving..." : (id ? "Update Application" : "Create Application")}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddApplication